import api from './api'

export interface AppConfiguration {
  id: string
  type: string
  key: string
  name: string
  description?: string
  application: string
  isEnabled: boolean
  isVisible: boolean
  sortOrder: number
  position?: 'top' | 'middle' | 'bottom'
  icon?: string
  route?: string
  color?: string
  config?: Record<string, any>
  metadata?: Record<string, any>
  featureFlag?: string
  permissions?: Record<string, any>
  createdAt: string
  updatedAt: string
}

export interface CreateAppConfigurationRequest {
  type: string
  key: string
  name: string
  description?: string
  application?: string
  isEnabled?: boolean
  isVisible?: boolean
  sortOrder?: number
  position?: 'top' | 'middle' | 'bottom'
  icon?: string
  route?: string
  color?: string
  config?: Record<string, any>
  metadata?: Record<string, any>
  featureFlag?: string
  permissions?: Record<string, any>
}

export interface UpdateAppConfigurationRequest extends Partial<CreateAppConfigurationRequest> {}

export interface AppConfigurationQuery {
  application?: string
  type?: string
  isEnabled?: boolean
  isVisible?: boolean
}

export interface BulkUpdateRequest {
  configurations: Array<{
    id: string
    sortOrder?: number
    isEnabled?: boolean
    isVisible?: boolean
  }>
}

export interface SidebarConfiguration {
  projectItems: AppConfiguration[]
  systemItems: AppConfiguration[]
  all: AppConfiguration[]
}

class AppConfigApi {
  // Get all app configurations
  async getConfigurations(query?: AppConfigurationQuery): Promise<AppConfiguration[]> {
    const params = new URLSearchParams()
    if (query?.application) params.append('application', query.application)
    if (query?.type) params.append('type', query.type)
    if (query?.isEnabled !== undefined) params.append('isEnabled', String(query.isEnabled))
    if (query?.isVisible !== undefined) params.append('isVisible', String(query.isVisible))
    
    const queryString = params.toString()
    const response = await api.get(`/app-configurations${queryString ? `?${queryString}` : ''}`)
    return response.data
  }

  // Get configurations for a specific application
  async getConfigurationsByApplication(application: string): Promise<AppConfiguration[]> {
    const response = await api.get(`/app-configurations/app/${application}`)
    return response.data
  }

  // Get configurations by application and type
  async getConfigurationsByApplicationAndType(application: string, type: string): Promise<AppConfiguration[]> {
    const response = await api.get(`/app-configurations/app/${application}/type/${type}`)
    return response.data
  }

  // Get configurations by type
  async getConfigurationsByType(type: string): Promise<AppConfiguration[]> {
    const response = await api.get(`/app-configurations/type/${type}`)
    return response.data
  }

  // Get sidebar configuration
  async getSidebarConfiguration(application: string = 'app'): Promise<SidebarConfiguration> {
    const response = await api.get(`/app-configurations/sidebar?application=${application}`)
    return response.data
  }

  // Get single configuration
  async getConfiguration(id: string): Promise<AppConfiguration> {
    const response = await api.get(`/app-configurations/${id}`)
    return response.data
  }

  // Create configuration
  async createConfiguration(data: CreateAppConfigurationRequest): Promise<AppConfiguration> {
    const response = await api.post('/app-configurations', data)
    return response.data
  }

  // Update configuration
  async updateConfiguration(id: string, data: UpdateAppConfigurationRequest): Promise<AppConfiguration> {
    const response = await api.patch(`/app-configurations/${id}`, data)
    return response.data
  }

  // Bulk update configurations
  async bulkUpdateConfigurations(data: BulkUpdateRequest): Promise<AppConfiguration[]> {
    const response = await api.patch('/app-configurations/bulk-update', data)
    return response.data
  }

  // Reorder configurations
  async reorderConfigurations(type: string, configurationIds: string[]): Promise<AppConfiguration[]> {
    const response = await api.patch(`/app-configurations/reorder/${type}`, { configurationIds })
    return response.data
  }

  // Toggle configuration
  async toggleConfiguration(id: string, isEnabled: boolean): Promise<AppConfiguration> {
    const response = await api.patch(`/app-configurations/${id}/toggle`, { isEnabled })
    return response.data
  }

  // Toggle visibility
  async toggleVisibility(id: string, isVisible: boolean): Promise<AppConfiguration> {
    const response = await api.patch(`/app-configurations/${id}/toggle-visibility`, { isVisible })
    return response.data
  }

  // Delete configuration
  async deleteConfiguration(id: string): Promise<void> {
    await api.delete(`/app-configurations/${id}`)
  }

  // Initialize default configurations
  async initializeDefaults(): Promise<void> {
    await api.post('/app-configurations/initialize-defaults')
  }

  // Initialize training app default configurations
  async initializeTrainingDefaults(): Promise<void> {
    await api.post('/app-configurations/initialize-training-defaults')
  }
}

export const appConfigApi = new AppConfigApi()
export default appConfigApi